import { formatString } from "@format-string/core";
import { Box, Chip, Avatar, Typography, alpha, useTheme, IconButton, Tooltip, Button } from "@mui/material";
import {
  Description as DescriptionIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Edit as EditIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import { cpfOurCnpj } from "src/shared/Utils/MaskCpfCnpj";
import { valuesPermissions } from "src/shared/Utils/ValuesPermissions";
import { UseVerifyIsPermissionsUser } from "src/Pages/components/hooks/UseVerifyIsPermissionsUser";
import { statusContract } from "../../../../Components/FormCancelContrato/FormCancelContrato/index";
import { ModernCard } from "./shared/ModernCard";
import { ModernInfoItem } from "./shared/ModernInfoItem";
import { objStatusContractColor } from "../../../../Components/table/TableContratos";
import { ModalEditarCodProposta } from "./ModalEditarCodProposta";
import { ContratoCompletoDialog } from "./ContratoCompleto/ContratoCompletoDialog";
import { UseGetContractById } from "../hooks/UseGetContractById";

interface ICardPropostaModern {
  idContract: string;
}

export const CardPropostaModern = ({ idContract }: ICardPropostaModern) => {
  const { valueContractInternal } = useContextContrato();
  const { handleVerifyPermissionItem } = UseVerifyIsPermissionsUser();
  const { handleGet } = UseGetContractById();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const [openModalEditarCodProposta, setOpenModalEditarCodProposta] = useState(false);
  const [openContratoCompleto, setOpenContratoCompleto] = useState(false);

  if (!valueContractInternal) return null;

  const isTypePF = valueContractInternal?.tipo_contrato === "PF";
  const statusColor = objStatusContractColor[valueContractInternal.status];
  const isDisabledEdit = handleVerifyPermissionItem(
    valuesPermissions.CONTRATO.EDITAR.id
  );

  return (
    <>
      <ModalEditarCodProposta
        openModal={openModalEditarCodProposta}
        setOpenModal={setOpenModalEditarCodProposta}
        idContract={idContract}
        onSuccess={() => handleGet(idContract)}
      />
      <ContratoCompletoDialog
        open={openContratoCompleto}
        idContract={idContract}
        onClose={() => setOpenContratoCompleto(false)}
      />

      <ModernCard
      sx={{
        borderLeft: `4px solid ${primaryColor}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
        }}
      >
        {/* Info principal */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 3 } }}>
          <Avatar
            sx={{
              width: { xs: 56, sm: 72, md: 88 },
              height: { xs: 56, sm: 72, md: 88 },
              bgcolor: alpha(primaryColor, 0.2),
              color: primaryColor,
            }}
          >
            <DescriptionIcon sx={{ fontSize: { xs: 28, sm: 36, md: 44 } }} />
          </Avatar>

          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 1.5,
                fontSize: { xs: "10px", sm: "11px" },
              }}
            >
              Proposta
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.35rem" },
                  mb: 0.5,
                }}
              >
                {valueContractInternal?.cod_proposta}
              </Typography>
              <Tooltip title="Editar código da proposta">
                <span>
                  <IconButton
                    size="small"
                    disabled={isDisabledEdit}
                    onClick={() => setOpenModalEditarCodProposta(true)}
                    sx={{ color: primaryColor }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              }}
            >
              {isTypePF
                ? valueContractInternal.titular_pf
                : valueContractInternal.razao_social_pj}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArticleIcon />}
            onClick={() => setOpenContratoCompleto(true)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Contrato completo
          </Button>
          <Chip
            label={statusContract[valueContractInternal.status]}
            sx={{
              bgcolor: statusColor,
              color: "#fff",
              fontWeight: 600,
              fontSize: { xs: "12px", sm: "13px" },
              px: { xs: 1, sm: 2 },
              py: { xs: 2, sm: 2.5 },
              borderRadius: "12px",
              boxShadow: `0 4px 14px ${alpha(statusColor, 0.4)}`,
            }}
          />
        </Box>
      </Box>

      {/* Informações de contato rápido */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 2, md: 4 },
          mt: 3,
          pt: 3,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <ModernInfoItem
          icon={<BadgeIcon />}
          label={isTypePF ? "CPF" : "CNPJ"}
          value={
            isTypePF
              ? (formatString({
                type: "cpf",
                value: valueContractInternal.cpf_cnpj,
              }) as string)
              : (formatString({
                type: "cnpj",
                value: valueContractInternal.cpf_cnpj,
              }) as string)
          }
        />
        <ModernInfoItem
          icon={<EmailIcon />}
          label="Email"
          value={valueContractInternal.email}
        />
        <ModernInfoItem
          icon={<PhoneIcon />}
          label="Telefone"
          value={cpfOurCnpj({
            text: valueContractInternal.telefone_1,
            type: "phone",
          })}
        />
      </Box>
    </ModernCard>
    </>
  );
};
